// src/components/chat/LiveChatWidget.jsx
// Phase 5 — Floating live chat widget for buyer-supplier messaging
// Uses polling (10s) against existing messaging API. Positioned bottom-left.

import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { messagingApi } from '@/api/services'
import { useAuth } from '@/context/AuthContext'
import { MessageSquare, X, Send, ChevronLeft } from 'lucide-react'

export default function LiveChatWidget() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [activeThread, setActiveThread] = useState(null)
  const [reply, setReply] = useState('')
  const bottomRef = useRef(null)

  // Only show for logged-in buyers
  if (!user || user.role !== 'buyer') return null

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-blue-500 text-white shadow-lg hover:bg-blue-600 transition-all flex items-center justify-center hover:scale-105"
          title="Live Chat"
        >
          <MessageSquare size={22} />
          <UnreadDot />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 left-6 z-50 w-80 h-[28rem] bg-white rounded-2xl shadow-2xl border flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-blue-500 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              {activeThread && (
                <button onClick={() => setActiveThread(null)} className="hover:bg-blue-600 rounded p-0.5">
                  <ChevronLeft size={16} />
                </button>
              )}
              <span className="font-bold text-sm">
                {activeThread ? 'Chat' : 'Messages'}
              </span>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-blue-600 rounded p-1">
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          {activeThread ? (
            <ThreadView
              threadId={activeThread}
              reply={reply}
              setReply={setReply}
              bottomRef={bottomRef}
              qc={qc}
            />
          ) : (
            <ThreadList onSelect={setActiveThread} />
          )}
        </div>
      )}
    </>
  )
}

function UnreadDot() {
  const { data } = useQuery({
    queryKey: ['chat-unread'],
    queryFn: () => messagingApi.unread().then(r => r.data),
    refetchInterval: 15000,
  })
  const count = data?.data?.count ?? data?.count ?? 0
  if (!count) return null
  return (
    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center">
      {count > 9 ? '9+' : count}
    </span>
  )
}

function ThreadList({ onSelect }) {
  const { data, isLoading } = useQuery({
    queryKey: ['chat-threads'],
    queryFn: () => messagingApi.inbox(),
    refetchInterval: 10000,
  })
  const threads = data?.data?.data || data?.data || []

  if (isLoading) return <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Loading...</div>
  if (!threads.length) return (
    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-4">
      <MessageSquare size={32} className="mb-2 opacity-50" />
      <p className="text-sm">No conversations yet</p>
      <p className="text-xs mt-1">Start a chat from any product page</p>
    </div>
  )

  return (
    <div className="flex-1 overflow-y-auto">
      {threads.map(t => (
        <button
          key={t.id}
          onClick={() => onSelect(t.id)}
          className="w-full text-left px-4 py-3 border-b hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm text-gray-900 truncate max-w-[180px]">
              {t.other_user?.business_name || t.other_user?.name || 'Supplier'}
            </span>
            {t.unread_count > 0 && (
              <span className="bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {t.unread_count}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate mt-0.5">{t.last_message?.body || 'No messages'}</p>
          {t.last_message?.created_at && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              {new Date(t.last_message.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </button>
      ))}
    </div>
  )
}

function ThreadView({ threadId, reply, setReply, bottomRef, qc }) {
  const { data } = useQuery({
    queryKey: ['chat-thread', threadId],
    queryFn: () => messagingApi.show(threadId),
    refetchInterval: 5000,
  })

  const replyMut = useMutation({
    mutationFn: () => messagingApi.reply('buyer', threadId, { body: reply }),
    onSuccess: () => {
      setReply('')
      qc.invalidateQueries({ queryKey: ['chat-thread', threadId] })
      qc.invalidateQueries({ queryKey: ['chat-threads'] })
    },
  })

  const thread = data?.data?.data || data?.data
  const messages = thread?.messages || []

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  return (
    <>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {messages.map(msg => (
          <div
            key={msg.id}
            className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
              msg.is_mine
                ? 'ml-auto bg-blue-500 text-white rounded-br-sm'
                : 'mr-auto bg-gray-100 text-gray-800 rounded-bl-sm'
            }`}
          >
            <p className="whitespace-pre-wrap break-words">{msg.body}</p>
            <p className={`text-[10px] mt-1 ${msg.is_mine ? 'text-blue-200' : 'text-gray-400'}`}>
              {new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Reply input */}
      <div className="p-2 border-t flex gap-1.5 flex-shrink-0">
        <input
          value={reply}
          onChange={e => setReply(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && reply.trim() && replyMut.mutate()}
          placeholder="Type a message..."
          className="flex-1 border rounded-full px-3 py-2 text-sm focus:ring-1 focus:ring-blue-300 outline-none"
        />
        <button
          onClick={() => reply.trim() && replyMut.mutate()}
          disabled={!reply.trim() || replyMut.isPending}
          className="w-9 h-9 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 disabled:opacity-50 transition-colors flex-shrink-0"
        >
          <Send size={14} />
        </button>
      </div>
    </>
  )
}
